import FileExchangePanel from '@/components/FileExchangePanel';
import { useI18n } from '@/i18n/I18nContext';
import { HStack, Skeleton, Stack, Text, useDisclosure } from '@chakra-ui/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable, { amountHeader } from 'src/components/DataTable';
import type { DataTableColumnDef as ColumnDef, DataTableCellContext as CellContext, DataTableRow as Row } from 'src/components/DataTable';
import { FileDownload, FileSelect } from 'src/components/FileSelect';
import { fundService } from 'src/service/FundService';
import Content from 'src/components/Content';
import { MemoLinkCell } from 'src/components/ReachLink';
import { formatElapsedMillis, formatOverview, formatPicker, toTime } from 'src/data/DateTimeUtil';
import WaitingSpinner from '@/components/WaitingSpinner';
import { differenceInMilliseconds } from 'date-fns';
import { LuTimer } from 'react-icons/lu';
import { Tag } from '@/components/ui/tag';
import { IdAndName } from '@/api/IdAndName';
import { AmountUtil } from '@/data/AmountUtil';
import { Fund } from '@/api/Fund';
import AddButton from '@/components/buttons/AddButton';
import { DropDownMenu } from '@/components/DropDownMenu';
import IdAndNameData from 'src/data/IdAndNameData';
import ConfirmDialog from '@/components/ConfirmDialog';
import { useNavigate } from 'react-router-dom';

const sortByProjectName = (projects: Map<number, string>) => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    return (rowA: Row<Fund>, rowB: Row<Fund>): number => {
        const valueA = projects.get(rowA.original.projectId)?.trim() ?? '';
        const valueB = projects.get(rowB.original.projectId)?.trim() ?? '';
        return collator.compare(valueA, valueB);
    };
};

const sortByFundDate = (rowA: Row<Fund>, rowB: Row<Fund>): number => toTime(rowA.original.fundDate) - toTime(rowB.original.fundDate);

const OverviewFunds = () => {
    const { t } = useI18n();
    const navigate = useNavigate();

    const [data, setData] = useState<Fund[]>([]);
    const [projects, setProjects] = useState<Map<number, string>>(new Map());
    const [deleteData, setDeleteData] = useState<IdAndNameData<number> | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingTime, setLoadingTime] = useState(0);

    const { open: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

    const loadAllData = useCallback((): Promise<void> => {
        const startTime = Date.now();
        setIsLoading(true);
        return fundService.listAll()
            .then(r => {
                setData(r.results.concat());
                setProjects(new Map(r.projects.map(p => [p.id, p.name])));
            })
            .finally(() => {
                setIsLoading(false);
                setLoadingTime(differenceInMilliseconds(Date.now(), startTime));
            });
    }, []);

    const onUpload = useCallback((): Promise<void> => loadAllData(), [loadAllData]);

    useEffect(() => {
        loadAllData();
    }, [loadAllData]);

    const AmountCell = memo<{ value: number; width: string }>(({ value, width }) => (
        <Text width={width} textAlign='right'>{AmountUtil.format(value)}</Text>
    ));

    const FundSourceCell = memo<{ value: string | null }>(({ value }) => (
        <Text fontWeight='bold'>{value ?? ''}</Text>
    ));

    const columns: ColumnDef<Fund>[] = useMemo(
        () => [
            {
                id: 'fundDate',
                header: 'Datum',
                accessorFn: (row: Fund) => formatOverview(row.fundDate),
                sortFn: sortByFundDate,
            },
            {
                id: 'budgetYear',
                header: 'Mittel Jahr',
                accessorKey: 'budgetYear',
            },
            {
                id: 'project',
                header: 'Projekt',
                accessorFn: (row: Fund) => ({ id: row.projectId, name: projects.get(row.projectId)?.trim() ?? '' }),
                cell: (info: CellContext<Fund, IdAndName>) => <MemoLinkCell value={info.getValue()} type="projects" />,
                sortFn: sortByProjectName(projects),
            },
            {
                id: 'donationCode',
                header: 'Kostenstelle',
                accessorKey: 'donationCode'
            },
            {
                id: 'fundSourceName',
                header: 'Fonds Quelle',
                accessorKey: 'fundSourceName',
                cell: (info: CellContext<Fund, string | null>) => <FundSourceCell value={info.getValue()} />
            },
            {
                id: 'amount',
                header: amountHeader({ name: 'Summe', width: '5rem', casing: 'none', fontWeight: 'normal', fontSize: '.9rem' }),
                accessorKey: 'amount',
                cell: (info: CellContext<Fund, number>) => <AmountCell value={info.getValue()} width="10rem" />
            },
            {
                id: 'comments',
                header: 'Kommentar',
                accessorKey: 'comments'
            },
            {
                id: 'edit',
                enableSorting: false,
                cell: (info) => {
                    const row = info.row.original;
                    return <DropDownMenu
                        onMenuView={() => navigate(`/funds/view/${row.id}`)}
                        onMenuEdit={() => {
                            navigate(`/funds/edit/${row.id}`);
                        }}
                        onMenuDelete={() => {
                            setDeleteData({
                                id: row.id,
                                name: `${projects.get(row.projectId)?.trim() ?? 'n/a'} / ${row.donationCode}`
                            });
                            onDeleteOpen();
                        }}
                    />;
                }
            }
        ], [projects, onDeleteOpen, navigate]
    );

    return (
        <Content
            title={t('overviews.funds.title')}
            description={t('overviews.funds.description')}
            actions={<>
                <FileExchangePanel>
                    <FileSelect path='/api/funds/upload' onUpload={onUpload} />
                    <FileDownload scope='all' path='/api/funds/download' name={`fonds-${formatPicker(new Date())}.xlsx`} />
                </FileExchangePanel>
                <AddButton caption={t('overviews.funds.create')} onClick={() => navigate('/funds/add')} />
            </>}
        >
            <Stack gap='6'>
                {isLoading ? (
                    <Stack py={5}>
                        <WaitingSpinner size='xl' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                        <Skeleton height='20px' />
                    </Stack>
                ) : (
                    <>
                        <HStack justify='flex-end' flexWrap='wrap'>
                            <Text fontSize='sm' color='fg.muted' textAlign='end' aria-live='polite'>{t('common.table.results', { count: data.length })}</Text>
                            <Tag variant='outline' startElement={<LuTimer />}>{formatElapsedMillis(loadingTime)}</Tag>
                        </HStack>
                        <DataTable
                            columns={columns}
                            data={data}
                            initialSortByColumn='fundDate'
                            initialSortByDesc={true}
                            hideColumnsOnXs={['project', 'comments', 'edit']}
                            onRowClick={row => {
                                navigate(`/funds/edit/${row.id}`);
                            }}
                        />
                        <ConfirmDialog
                            heading={`${deleteData?.name} Löschen`}
                            isOpen={isDeleteOpen}
                            onClose={onDeleteClose}
                            onConfirm={async (): Promise<void> => {
                                if (!deleteData) {
                                    return;
                                }
                                await fundService.remove(deleteData.id);
                                await loadAllData();
                            }}
                        />
                    </>
                )}
            </Stack>
        </Content>
    );
}

export default OverviewFunds
